import { collection, doc, setDoc, getDoc, updateDoc, deleteDoc, query, where, getDocs, increment } from 'firebase/firestore';
import { db } from './firebase.js';
import { getCurrentUser } from './auth.js';
import { loadLinkedUserData, setLinkedAccount, loadUserData } from './budget.js';

/**
 * Account linking and sharing module for BudgetApp
 */

// Security configuration
const LINK_SECURITY = {
  codeLength: 6,            // 6 characters for easier sharing
  maxAttempts: 10,          // Max 10 attempts per code
  attemptWindowMinutes: 15, // 15 minute rate limit window
  codeExpireMinutes: 60,    // Codes expire in 1 hour
  maxCodesPerUser: 3        // Max 3 active codes per user
};

// Global link state
let linkedAccountId = null;
let isLinkedAccount = false;
let linkCode = null;
let currentGeneratedCode = null;
let linkingState = 'none'; // 'none', 'pending', 'linked', 'timeout'
let hasFollowers = false;
let linkedUserEmail = null;

// Track failed attempts per user
let linkAttempts = {};

// Generate a secure 6-character, case-insensitive code
function generateSecureLinkCode() {
  // Using only uppercase letters and numbers for simplicity
  // 36^6 = 2.1 billion combinations (still secure with rate limiting)
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < LINK_SECURITY.codeLength; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// Check if user has exceeded rate limit
function checkRateLimit(userId) {
  const now = Date.now();
  const windowStart = now - (LINK_SECURITY.attemptWindowMinutes * 60 * 1000);
  
  if (!linkAttempts[userId]) {
    linkAttempts[userId] = [];
  }
  
  // Clean old attempts
  linkAttempts[userId] = linkAttempts[userId].filter(time => time > windowStart);
  
  return linkAttempts[userId].length < LINK_SECURITY.maxAttempts;
}

// Record a failed attempt
function recordFailedAttempt(userId) {
  if (!linkAttempts[userId]) {
    linkAttempts[userId] = [];
  }
  linkAttempts[userId].push(Date.now());
}

// Get linked user's email
async function getLinkedUserEmail(userId) {
  try {
    if (!userId) return null;
    
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().email || null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching linked user email:', error);
    return null;
  }
}

// Clean up expired codes created by the current user only (security best practice)
export async function cleanupExpiredCodes() {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) return;
    
    const expiredTime = new Date(Date.now() - (LINK_SECURITY.codeExpireMinutes * 60 * 1000));
    
    // Query only codes created by the current user (single-field query for better security rule compatibility)
    const userCodesQuery = query(
      collection(db, 'linkCodes'),
      where('createdBy', '==', currentUser.uid)
    );
    
    const userCodes = await getDocs(userCodesQuery);
    
    // Filter expired codes on the client side
    const expiredCodes = userCodes.docs.filter(doc => {
      const codeData = doc.data();
      const createdAt = codeData.createdAt.toDate ? codeData.createdAt.toDate() : codeData.createdAt;
      return createdAt < expiredTime;
    });
    
    // Delete expired codes
    if (expiredCodes.length > 0) {
      const deletePromises = expiredCodes.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log(`Cleaned up ${deletePromises.length} expired codes created by current user`);
    }
  } catch (error) {
    console.error('Error cleaning up expired codes:', error);
  }
}

// Update link status indicator
export async function updateLinkStatusIndicator() {
  const indicator = document.getElementById('link-status-indicator');
  const tooltip = document.getElementById('link-tooltip');
  
  if (!indicator || !tooltip) return;
  
  // Remove all state classes
  indicator.classList.remove('linked', 'pending', 'timeout', 'hidden');
  
  if (isLinkedAccount) {
    indicator.classList.add('linked');
    
    // Get linked user email if not already cached
    if (!linkedUserEmail && linkedAccountId) {
      linkedUserEmail = await getLinkedUserEmail(linkedAccountId);
    }
    
    tooltip.textContent = linkedUserEmail 
      ? `Linked Budget with: ${linkedUserEmail}`
      : 'Linked Budget';
    indicator.style.display = 'flex';
  } else if (hasFollowers) {
    indicator.classList.add('linked');
    tooltip.textContent = 'Shared Budget';
    indicator.style.display = 'flex';
  } else if (linkingState === 'pending') {
    indicator.classList.add('pending');
    tooltip.textContent = 'Request Pending';
    indicator.style.display = 'flex';
  } else if (linkingState === 'timeout') {
    indicator.classList.add('timeout');
    tooltip.textContent = 'Request Timed Out';
    indicator.style.display = 'flex';
  } else {
    indicator.classList.add('hidden');
  }
  
  // Update hamburger menu buttons based on linking state
  updateHamburgerMenuButtons();
}

// Update hamburger menu buttons based on linking state
function updateHamburgerMenuButtons() {
  const shareBtn = document.getElementById('household-share-btn');
  const joinBtn = document.getElementById('household-join-btn');
  const unlinkBtn = document.getElementById('household-unlink-btn');
  
  if (isLinkedAccount) {
    // User is a follower - show only unlink button
    if (shareBtn) shareBtn.style.display = 'none';
    if (joinBtn) joinBtn.style.display = 'none';
    if (unlinkBtn) unlinkBtn.style.display = 'block';
  } else if (hasFollowers) {
    // User is owner with followers - show only share button (which becomes "Remove Access")
    if (shareBtn) shareBtn.style.display = 'block';
    if (joinBtn) joinBtn.style.display = 'none';
    if (unlinkBtn) unlinkBtn.style.display = 'none';
  } else {
    // User is budget owner with no followers - show share and join buttons
    if (shareBtn) shareBtn.style.display = 'block';
    if (joinBtn) joinBtn.style.display = 'block';
    if (unlinkBtn) unlinkBtn.style.display = 'none';
  }
}

// Show link interface (when clicking the status indicator)
export function showLinkInterface() {
  if (isLinkedAccount) {
    // Show unlink option
    if (confirm('You are currently linked to a shared budget. Would you like to unlink?')) {
      unlinkAccounts();
    }
  } else {
    // Show link generation modal
    showLinkCodeModal();
  }
}

// Show link code generation modal
export async function showLinkCodeModal() {
  // Close the menu
  const { closeMenu } = await import('./utils/mobile.js');
  closeMenu();
  
  const modal = document.getElementById('link-code-modal');
  if (modal) {
    // Reset scroll position to top
    window.scrollTo(0, 0);
    
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Generate a code immediately
    generateNewCode();
  }
}

// Hide link code generation modal
export async function hideLinkCodeModal() {
  const modal = document.getElementById('link-code-modal');
  if (modal) {
    modal.classList.remove('active');
    document.body.style.overflow = '';
    
    // Update status to show minimized indicator
    if (!isLinkedAccount && currentGeneratedCode) {
      linkingState = 'pending';
      await updateLinkStatusIndicator();
      
      // Set timeout to change status
      setTimeout(async () => {
        if (linkingState === 'pending') {
          linkingState = 'timeout';
          await updateLinkStatusIndicator();
        }
      }, LINK_SECURITY.codeExpireMinutes * 60 * 1000);
    }
  }
}

// Show link redemption modal
export function showLinkRedemptionModal() {
  const modal = document.getElementById('link-redemption-modal');
  if (modal) {
    // Reset scroll position to top
    window.scrollTo(0, 0);
    
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Ensure event listeners are set up (in case of timing issues)
    setupLinkSharingEventListeners();
    
    // Focus on first input
    setTimeout(() => {
      const firstInput = document.querySelector('.code-input-box');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);
  }
}

// Hide link redemption modal
export function hideLinkRedemptionModal() {
  const modal = document.getElementById('link-redemption-modal');
  if (modal) {
    modal.classList.remove('active');
    document.body.style.overflow = '';
    clearCodeInputs();
    
    // Clear listener markers so they can be re-attached next time
    const codeInputs = document.querySelectorAll('#link-redemption-modal .code-input-box');
    codeInputs.forEach(input => {
      input.removeAttribute('data-listeners-attached');
    });
  }
}

// Generate new link code
export async function generateNewCode() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  try {
    // Clean up old codes first
    await cleanupExpiredCodes();
    
    // Check if user has too many active codes
    const userCodesQuery = query(
      collection(db, 'linkCodes'),
      where('createdBy', '==', currentUser.uid)
    );
    
    const userCodes = await getDocs(userCodesQuery);
    
    if (userCodes.docs.length >= LINK_SECURITY.maxCodesPerUser) {
      alert('You have too many active codes. Please wait for them to expire or delete old ones.');
      return;
    }
    
    // Generate new code (collision probability is very low with 36^6 combinations)
    const newCode = generateSecureLinkCode();
    const expiresAt = new Date(Date.now() + (LINK_SECURITY.codeExpireMinutes * 60 * 1000));
    
    // Create the code - since we cleaned up old codes and probability of collision is minimal,
    // we can safely create without checking existence
    await setDoc(doc(db, 'linkCodes', newCode), {
      code: newCode,
      createdBy: currentUser.uid,
      createdAt: new Date(),
      expiresAt: expiresAt,
      used: false,
      attempts: 0
    });
    
    currentGeneratedCode = newCode;
    displayGeneratedCode(newCode);
    
  } catch (error) {
    console.error('Error generating code:', error);
    alert('Failed to generate link code. Please try again.');
  }
}

// Display generated code in the modal
function displayGeneratedCode(code) {
  const codeDigitsEl = document.getElementById('code-digits');
  if (codeDigitsEl) {
    codeDigitsEl.innerHTML = '';
    
    for (let i = 0; i < code.length; i++) {
      const digitEl = document.createElement('span');
      digitEl.className = 'code-digit';
      digitEl.textContent = code[i];
      codeDigitsEl.appendChild(digitEl);
    }
  }
}

// Copy code to clipboard
export function copyCodeToClipboard() {
  if (currentGeneratedCode) {
    navigator.clipboard.writeText(currentGeneratedCode).then(() => {
      alert('Code copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentGeneratedCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Code copied to clipboard!');
    });
  }
}

// Submit link code for redemption
export async function submitLinkCode() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  const codeInputs = document.querySelectorAll('.code-input-box');
  const code = Array.from(codeInputs).map(input => input.value.toUpperCase()).join('');
  
  if (code.length !== LINK_SECURITY.codeLength) {
    showLinkError('Please enter a complete 6-character code.');
    return;
  }
  
  // Check rate limit
  if (!checkRateLimit(currentUser.uid)) {
    showLinkError('Too many attempts. Please wait before trying again.');
    return;
  }
  
  try {
    // First, get the link code document to find the creator
    const linkCodeDoc = await getDoc(doc(db, 'linkCodes', code));
    
    if (!linkCodeDoc.exists()) {
      showLinkError('Invalid or expired code.');
      return;
    }
    
    const linkData = linkCodeDoc.data();
    console.log('Link code data:', linkData);
    
    // Check if code is expired or already used
    const now = new Date();
    const codeAge = now - linkData.createdAt.toDate();
    if (codeAge > LINK_SECURITY.codeExpiry) {
      showLinkError('This code has expired.');
      return;
    }
    
    if (linkData.used) {
      showLinkError('This code has already been used.');
      return;
    }
    
    const creatorId = linkData.createdBy;
    console.log('Link creator ID:', creatorId);
    
    // Mark code as used
    await updateDoc(doc(db, 'linkCodes', code), {
      used: true,
      usedBy: currentUser.uid,
      usedAt: new Date()
    });
    
    // Create link from current user to creator account
    // Note: We only update the current user's document for security reasons
    // The creator doesn't need a back-link since they own the data being shared
    await updateDoc(doc(db, 'users', currentUser.uid), {
      linkedAccount: creatorId
    });
    
    console.log('Account linking completed successfully');
    
    // Success - code was valid and redeemed
    await handleSuccessfulRedemption(code, creatorId);
    
  } catch (error) {
    console.error('Error submitting link code:', error);
    
    if (error.code === 'permission-denied') {
      // Code invalid, expired, used, or attempt limit reached
      await handleRedemptionFailure(code, currentUser.uid);
    } else {
      showLinkError('An error occurred. Please try again.');
    }
  }
}

// Handle successful code redemption
async function handleSuccessfulRedemption(code, creatorId) {
  try {
    console.log('Handling successful redemption with creatorId:', creatorId);
    
    linkedAccountId = creatorId;
    isLinkedAccount = true;
    linkingState = 'linked';
    linkedUserEmail = null; // Clear cached email for new account
    
    // Sync the linking state with budget.js
    setLinkedAccount(linkedAccountId, true);
    
    // Clear expense cache when linking state changes to ensure fresh data
    if (typeof window !== 'undefined' && window.clearExpenseCache) {
      console.log('🗑️ [Linking Debug] Clearing expense cache after account linking');
      window.clearExpenseCache();
    }
    
    await updateLinkStatusIndicator();
    hideLinkRedemptionModal();
    
    // Load linked account data
    console.log('Loading linked account data for:', linkedAccountId);
    await loadLinkedUserData();
    
    alert('Successfully linked to shared budget!');
  } catch (error) {
    console.error('Error handling successful redemption:', error);
    hideLinkRedemptionModal();
    alert('Code redeemed successfully! Please refresh to see linked budget.');
  }
}

// Handle redemption failure
async function handleRedemptionFailure(code, userId) {
  recordFailedAttempt(userId);
  
  try {
    // Try to increment attempts - this may also fail if code doesn't exist
    await updateDoc(doc(db, 'linkCodes', code), {
      attempts: increment(1)
    });
    showLinkError('Invalid or expired code. Please check and try again.');
  } catch (error) {
    // Code doesn't exist or other permission issue
    showLinkError('Invalid code. Please check and try again.');
  }
}

// Show link error message
function showLinkError(message) {
  const errorEl = document.getElementById('link-error-message');
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
    
    setTimeout(() => {
      errorEl.style.display = 'none';
    }, 5000);
  }
}

// Validate code inputs and enable/disable submit button
export function validateCodeInputs() {
  const codeInputs = document.querySelectorAll('#link-redemption-modal .code-input-box');
  const allFilled = Array.from(codeInputs).every(inp => inp.value.length === 1);
  const submitBtn = document.getElementById('submit-link-code');
  console.log('Manual validation - All inputs filled:', allFilled, 'Values:', Array.from(codeInputs).map(inp => inp.value));
  if (submitBtn) {
    submitBtn.disabled = !allFilled;
    console.log('Manual validation - Submit button disabled:', submitBtn.disabled);
  }
  return allFilled;
}

// Clear code input boxes
export function clearCodeInputs() {
  const codeInputs = document.querySelectorAll('#link-redemption-modal .code-input-box');
  codeInputs.forEach(input => {
    input.value = '';
    input.removeAttribute('data-listeners-attached'); // Allow re-setup of listeners
  });
  
  const errorEl = document.getElementById('link-error-message');
  if (errorEl) {
    errorEl.style.display = 'none';
  }
  
  const submitBtn = document.getElementById('submit-link-code');
  if (submitBtn) {
    submitBtn.disabled = true;
  }
}

// Unlink accounts
export async function unlinkAccounts() {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    // Remove linkedAccount from Firestore
    await updateDoc(doc(db, 'users', currentUser.uid), {
      linkedAccount: null
    });
    
    // Update local state
    linkedAccountId = null;
    isLinkedAccount = false;
    linkingState = 'none';
    linkedUserEmail = null; // Clear cached email when unlinking
    
    // Sync the linking state with budget.js
    setLinkedAccount(null, false);
    
    // Clear expense cache when unlinking to ensure fresh data
    if (typeof window !== 'undefined' && window.clearExpenseCache) {
      console.log('🗑️ [Linking Debug] Clearing expense cache after account unlinking');
      window.clearExpenseCache();
    }
    
    await updateLinkStatusIndicator();
    
    // Reload user's own data
    await loadUserData();
    
    alert('Successfully unlinked from shared budget.');
    
  } catch (error) {
    console.error('Error unlinking accounts:', error);
    alert('Failed to unlink accounts. Please try again.');
  }
}

// Handle household unlink button click (for followers)
export async function unlinkHousehold() {
  if (confirm('Are you sure you want to unlink from this shared budget? You will lose access to the shared data.')) {
    await unlinkAccounts();
  }
}

// Export function to manually update hamburger menu (for debugging/forcing updates)
export function updateHamburgerMenuButtonsManually() {
  updateHamburgerMenuButtons();
}

// Setup link sharing event listeners  
export function setupLinkSharingEventListeners() {
  // Only set up once to avoid duplicates
  if (document.querySelector('.code-input-box[data-listeners-attached]')) {
    console.log('Link sharing event listeners already set up');
    return;
  }
  
  // Code input boxes for auto-advance - get only the 6 inputs in the redemption modal
  const codeInputs = document.querySelectorAll('#link-redemption-modal .code-input-box');
  console.log('Setting up link sharing event listeners for', codeInputs.length, 'inputs');
  
  if (codeInputs.length !== 6) {
    console.error('Expected 6 code input boxes, found', codeInputs.length);
    return;
  }
  
  codeInputs.forEach((input, index) => {
    // Mark as having listeners attached
    input.setAttribute('data-listeners-attached', 'true');
    
    input.addEventListener('input', (e) => {
      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); // Only allow alphanumeric
      e.target.value = value;
      
      // Auto-advance to next input
      if (value && index < codeInputs.length - 1) {
        codeInputs[index + 1].focus();
      }
      
      // Enable/disable submit button
      const allFilled = Array.from(codeInputs).every(inp => inp.value.length === 1);
      const submitBtn = document.getElementById('submit-link-code');
      console.log('All inputs filled:', allFilled, 'Values:', Array.from(codeInputs).map(inp => inp.value));
      if (submitBtn) {
        submitBtn.disabled = !allFilled;
        console.log('Submit button disabled:', submitBtn.disabled);
      }
    });
    
    input.addEventListener('keydown', (e) => {
      // Handle backspace to go to previous input
      if (e.key === 'Backspace' && !e.target.value && index > 0) {
        codeInputs[index - 1].focus();
      }
    });
  });
}

// Get linking state
export function getLinkingState() {
  return {
    linkedAccountId,
    isLinkedAccount,
    linkingState,
    currentGeneratedCode
  };
}

// Check if current user has followers
export async function checkForFollowers() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    hasFollowers = false;
    return false;
  }
  
  try {
    // Query all users to see if any have linkedAccount pointing to current user
    const usersQuery = query(
      collection(db, 'users'),
      where('linkedAccount', '==', currentUser.uid)
    );
    const querySnapshot = await getDocs(usersQuery);
    
    hasFollowers = querySnapshot.size > 0;
    console.log(`Current user has ${querySnapshot.size} followers`);
    
    await updateLinkStatusIndicator();
    updateShareAccessButton();
    return hasFollowers;
  } catch (error) {
    console.error('Error checking for followers:', error);
    hasFollowers = false;
    return false;
  }
}

// Set linking state (for initialization)
export async function setLinkingState(accountId, isLinked) {
  linkedAccountId = accountId;
  isLinkedAccount = isLinked;
  linkingState = isLinked ? 'linked' : 'none';
  linkedUserEmail = null; // Clear cached email when state changes
  
  // Sync the linking state with budget.js
  setLinkedAccount(accountId, isLinked);
  
  await updateLinkStatusIndicator();
}

// Handle Share Access / Remove Access button click
export async function handleShareAccessClick() {
  if (hasFollowers) {
    // Show remove access options
    await showRemoveAccessModal();
  } else {
    // Show sharing modal
    showLinkCodeModal();
  }
}

// Update the Share Access button text based on follower status
export function updateShareAccessButton() {
  const shareBtn = document.getElementById('household-share-btn');
  if (!shareBtn) return;
  
  if (hasFollowers) {
    shareBtn.innerHTML = '🔓 Remove Access';
    shareBtn.title = 'Remove follower access to your budget';
  } else {
    shareBtn.innerHTML = '🔗 Share Access';
    shareBtn.title = 'Share your budget with others';
  }
}

// Show modal to remove access for followers
async function showRemoveAccessModal() {
  const currentUser = getCurrentUser();
  if (!currentUser) return;
  
  try {
    // Get list of followers
    const usersQuery = query(
      collection(db, 'users'),
      where('linkedAccount', '==', currentUser.uid)
    );
    const querySnapshot = await getDocs(usersQuery);
    
    if (querySnapshot.empty) {
      alert('No followers found.');
      return;
    }
    
    const followers = [];
    querySnapshot.docs.forEach(doc => {
      followers.push({
        id: doc.id,
        email: doc.data().email || 'Unknown user',
        data: doc.data()
      });
    });
    
    // Simple confirmation dialog for now
    const followerEmails = followers.map(f => f.email).join(', ');
    const confirmed = confirm(`Remove access for: ${followerEmails}?\n\nThis will disconnect them from your budget.`);
    
    if (confirmed) {
      await removeAllFollowers(followers);
    }
  } catch (error) {
    console.error('Error showing remove access modal:', error);
    alert('Error loading followers. Please try again.');
  }
}

// Remove all followers
async function removeAllFollowers(followers) {
  try {
    const promises = followers.map(follower => 
      updateDoc(doc(db, 'users', follower.id), {
        linkedAccount: null
      })
    );
    
    await Promise.all(promises);
    
    // Update local state
    hasFollowers = false;
    await updateLinkStatusIndicator();
    updateShareAccessButton();
    
    alert(`Successfully removed access for ${followers.length} follower(s).`);
  } catch (error) {
    console.error('Error removing followers:', error);
    alert('Error removing followers. Please try again.');
  }
}

// Make functions available globally for HTML onclick handlers
if (typeof window !== 'undefined') {
  window.showLinkCodeModal = showLinkCodeModal;
  window.handleShareAccessClick = handleShareAccessClick;
  window.hideLinkCodeModal = hideLinkCodeModal;
  window.showLinkRedemptionModal = showLinkRedemptionModal;
  window.hideLinkRedemptionModal = hideLinkRedemptionModal;
  window.generateNewCode = generateNewCode;
  window.copyCodeToClipboard = copyCodeToClipboard;
  window.submitLinkCode = submitLinkCode;
  window.clearCodeInputs = clearCodeInputs;
  window.showLinkInterface = showLinkInterface;
}